import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { useGetNbaPlayerByIdQuery } from "../../store/api/nbaApi";
import CareerArcChart from "../../components/charts/CareerArcChart";
import NbaCompareSlot from "../../components/nba/NbaCompareSlot";
import NbaHeadToHeadTable from "../../components/nba/NbaHeadToHeadTable";
import NbaCompareInspiration from "../../components/nba/NbaCompareInspiration";
import CommunityDebateCta from "../../components/community/CommunityDebateCta";
import { getPlayerAccent } from "../../components/compare/comparePlayerVisuals";
import {
  CompareChartCard,
  ComparePageFrame,
  CompareSlotsBar,
  CompareVsSeparator,
} from "../../components/compare/ComparePageChrome";
import {
  COMPARE_CHART_HEIGHT,
  COMPARE_MAX_PLAYERS,
  COMPARE_PAGE_SUBTITLE,
  COMPARE_PAGE_TITLE,
  assignIdToSlots,
  getCompareTimeframe,
} from "../../components/compare/compareShell";
import {
  NBA_STAT_GROUPS,
  buildNbaCompareNamedSeries,
  getNbaCompareStat,
  getNbaCompareStats,
  getNbaDefaultChartStat,
  playerHasNbaSeasons,
} from "../../adapters/nbaPlayerAdapter";

const emptySlots = (count) => Array.from({ length: count }, () => "");

const NbaCompare = () => {
  const [searchParams] = useSearchParams();
  const presetLoadedRef = useRef(false);
  const [playerIds, setPlayerIds] = useState(() => emptySlots(2));
  const [groupId, setGroupId] = useState("totals");
  const [statId, setStatId] = useState(getNbaDefaultChartStat("totals"));
  const [normalizeByCareer, setNormalizeByCareer] = useState(true);

  const query0 = useGetNbaPlayerByIdQuery(playerIds[0], { skip: !playerIds[0] });
  const query1 = useGetNbaPlayerByIdQuery(playerIds[1], { skip: !playerIds[1] });
  const query2 = useGetNbaPlayerByIdQuery(playerIds[2], { skip: !playerIds[2] });
  const query3 = useGetNbaPlayerByIdQuery(playerIds[3], { skip: !playerIds[3] });
  const query4 = useGetNbaPlayerByIdQuery(playerIds[4], { skip: !playerIds[4] });
  const queries = [query0, query1, query2, query3, query4];

  useEffect(() => {
    if (presetLoadedRef.current) return;
    const param = searchParams.get("players");
    if (!param) return;
    const ids = param
      .split(",")
      .map((id) => id.trim())
      .filter(Boolean)
      .slice(0, COMPARE_MAX_PLAYERS);
    if (ids.length === 0) return;
    setPlayerIds(ids.length === 1 ? [...ids, ""] : ids);
    presetLoadedRef.current = true;
  }, [searchParams]);

  const compareStats = getNbaCompareStats(groupId);
  const selectedStat = getNbaCompareStat(statId, groupId);
  const timeframe = getCompareTimeframe(normalizeByCareer);
  const players = playerIds
    .map((id, index) => (id ? queries[index]?.data : null))
    .filter(Boolean);

  const loadedPlayerCount = players.length;
  const showComparison = loadedPlayerCount >= 2;
  const isLoadingComparison = playerIds.some(
    (id, index) => id && queries[index]?.isLoading
  );
  const isLoadingPresetComparison = presetLoadedRef.current && isLoadingComparison;
  const canShowChart = showComparison && players.some(playerHasNbaSeasons);

  const namedSeries = useMemo(
    () => buildNbaCompareNamedSeries(players, selectedStat.id),
    [players, selectedStat.id]
  );

  const handleSelect = (index, player) => {
    setPlayerIds((current) =>
      current.map((id, slot) => (slot === index ? player?.playerId || "" : id))
    );
  };

  const handleClear = (index) => {
    setPlayerIds((current) => {
      if (current.length > 2) {
        return current.filter((_, slot) => slot !== index);
      }
      return current.map((id, slot) => (slot === index ? "" : id));
    });
  };

  const applyPreset = (ids) => {
    setPlayerIds(ids);
    presetLoadedRef.current = true;
  };

  const handleInspirationSelect = (player) => {
    setPlayerIds((current) =>
      assignIdToSlots(current, player.playerId, COMPARE_MAX_PLAYERS)
    );
  };

  const handleGroupChange = (nextGroup) => {
    setGroupId(nextGroup);
    setStatId(getNbaDefaultChartStat(nextGroup));
  };

  const renderChartBody = () => {
    if (!showComparison || !canShowChart) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          {isLoadingPresetComparison ? (
            <CircularProgress />
          ) : (
            <Typography variant="body2" color="text.secondary">
              {isLoadingComparison
                ? "Loading player stats…"
                : loadedPlayerCount === 1
                  ? "Add one more player to start comparing"
                  : "Search or pick players below to compare"}
            </Typography>
          )}
        </Box>
      );
    }

    return (
      <CareerArcChart
        namedSeries={namedSeries}
        xAxisName={timeframe.axisName}
        yAxisName={selectedStat.label}
        mode={timeframe.mode}
        height={COMPARE_CHART_HEIGHT}
        emptyMessage={`No season values for ${selectedStat.label}.`}
      />
    );
  };

  return (
    <ComparePageFrame title={COMPARE_PAGE_TITLE} subtitle={COMPARE_PAGE_SUBTITLE}>
      <CompareSlotsBar
        onAdd={() => setPlayerIds((current) => [...current, ""])}
        canAdd={playerIds.length < COMPARE_MAX_PLAYERS}
        atMax={playerIds.length >= COMPARE_MAX_PLAYERS}
      >
        {playerIds.map((playerId, index) => (
          <React.Fragment key={`${index}-${playerId || "empty"}`}>
            {index > 0 && <CompareVsSeparator />}
            <NbaCompareSlot
              index={index}
              playerId={playerId}
              player={queries[index]?.data}
              isLoading={Boolean(playerId) && queries[index]?.isLoading}
              accent={getPlayerAccent(index)}
              showClear={playerIds.length > 2}
              onSelect={(player) => handleSelect(index, player)}
              onClear={() => handleClear(index)}
            />
          </React.Fragment>
        ))}
      </CompareSlotsBar>

      <NbaCompareInspiration
        selectedIds={playerIds}
        onSelect={handleInspirationSelect}
        onSelectMatchup={applyPreset}
      />

      <CommunityDebateCta sport="nba" playerIds={playerIds} />

      <CompareChartCard
        groups={NBA_STAT_GROUPS.map((group) => ({
          value: group.id,
          label: group.label,
        }))}
        groupValue={groupId}
        onGroupChange={handleGroupChange}
        stats={compareStats}
        statValue={selectedStat.id}
        onStatChange={setStatId}
        timeframe={timeframe}
        showTimeframe={showComparison}
        onToggleTimeframe={() => setNormalizeByCareer((current) => !current)}
      >
        {renderChartBody()}
      </CompareChartCard>

      <NbaHeadToHeadTable
        players={players}
        groupId={groupId}
        selectedStatId={selectedStat.id}
        showComparison={showComparison}
      />
    </ComparePageFrame>
  );
};

export default NbaCompare;
